import type { Modification, TransactionRule } from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { EditTransactionRuleModal } from '../EditTransactionRuleModal';
import { RuleDeletionAlert } from '../RuleDeletionAlert';
import { RuleUnlockAlert } from '../RuleUnlockAlert';
import {
  RulesContent,
  type RulesContentListProps,
  RulesManagerContext,
  RulesManagerEmptySearch,
  RulesManagerFilters,
} from '../RulesManager';
import { TransactionRulePreview } from '../TransactionRulePreview';
import { TransactionListItem } from './TransactionListItem';
import { type Filters, useFilteredRules } from './useFilteredRules';
import { duplicateRuleName } from './utils';

export interface TransactionsContentProps {
  transactionModifications: Modification<'transaction_rule'>[];
  editTransactionModification: (
    transactionModification: Modification<'transaction_rule'>,
    refresh?: boolean
  ) => Promise<unknown>;
  deleteTransactionModification: (
    transactionModification: Modification<'transaction_rule'>
  ) => Promise<unknown>;
  reorderTransactionModification: (
    moved: Modification<'transaction_rule'>,
    after?: Modification<'transaction_rule'>
  ) => Promise<unknown>;
  createTransactionRule: (
    transactionrule: TransactionRule & {
      isActive?: boolean;
      isLocked?: boolean;
    },
    refresh?: boolean
  ) => Promise<unknown>;
}
export const TransactionsContent = ({
  transactionModifications,
  editTransactionModification,
  deleteTransactionModification,
  reorderTransactionModification,
  createTransactionRule,
}: TransactionsContentProps) => {
  const { onLearnMoreClick } = useContext(RulesManagerContext);
  const { editorDataRulesReorder } = useFlags();
  const [showEditModal, setShowEditModal] = useBoolean();
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [showUnlockAlert, setShowUnlockAlert] = useBoolean();
  const [selectedTransaction, setSelectedTransaction] =
    useState<Modification<'transaction_rule'>>(null);

  const {
    filteredRules,
    setFilter,
    filter,
    isFiltersActive,
    searchValue,
    setSearchValue,
  } = useFilteredRules(transactionModifications);

  const toggleActive = async (
    transactionModification: Modification<'transaction_rule'>
  ) => {
    try {
      await editTransactionModification({
        ...transactionModification,
        isActive: !transactionModification.isActive,
      });
    } catch (error) {
      console.error(error);
      notify.error('Failed to toggle rule activation');
    }
  };

  const toggleLock = async (
    transactionModification: Modification<'transaction_rule'>
  ) => {
    if (transactionModification.isLocked) {
      setSelectedTransaction(transactionModification);
      setShowUnlockAlert.setTrue();
      return;
    }

    try {
      await editTransactionModification({
        ...transactionModification,
        isLocked: true,
      });
    } catch (error) {
      console.error(error);
      notify.error('Failed to lock rule');
    }
  };

  const onReorderModifications: RulesContentListProps<
    Modification<'transaction_rule'>
  >['onOrderChange'] = async ({ rules, destinationIndex }) => {
    const moved = rules[destinationIndex].rule;
    const after = rules[destinationIndex + 1]?.rule;
    await reorderTransactionModification(moved, after).catch(() => {
      notify.snackbar({
        type: 'error',
        label: 'Failed to reorder. Please try again',
      });
    });
  };

  return (
    <>
      <RulesContent>
        {!!transactionModifications?.length && (
          <RulesManagerFilters<Filters['locked']>
            onSearchChange={setSearchValue}
            onFiltersChange={setFilter}
            dropdownItems={[
              { id: 'locked', label: 'Locked' },
              { id: 'unlocked', label: 'Unlocked' },
            ]}
            searchValue={searchValue}
            selectedFilter={filter}
          />
        )}
        {filteredRules.length ? (
          <RulesContent.List<Modification<'transaction_rule'>>
            draggable={!isFiltersActive && editorDataRulesReorder}
            onOrderChange={onReorderModifications}
            rules={filteredRules.map((transactionRule) => ({
              id: transactionRule.id,
              rule: transactionRule,
            }))}
            renderItem={({ ruleItem }) => (
              <TransactionListItem
                onEdit={() => {
                  setSelectedTransaction(ruleItem.rule);
                  setShowEditModal.setTrue();
                }}
                onToggleActivate={() => toggleActive(ruleItem.rule)}
                onToggleLock={() => toggleLock(ruleItem.rule)}
                onDelete={() => {
                  setSelectedTransaction(ruleItem.rule);
                  setShowDeletionAlert.setTrue();
                }}
                transactionModification={ruleItem.rule}
                onDuplicate={({ content, isActive, isLocked }) =>
                  createTransactionRule(
                    {
                      ...content,
                      isActive,
                      isLocked,
                      id: uuid(),
                      name: duplicateRuleName(
                        content.name,
                        transactionModifications.map(
                          ({ content }) => content.name
                        )
                      ),
                      rules: content.rules.map((rule) => ({
                        ...rule,
                        id: uuid(),
                      })),
                    },
                    false
                  )
                }
              />
            )}
          />
        ) : (
          <RulesManagerEmptySearch />
        )}
      </RulesContent>
      <EditTransactionRuleModal
        onLearnMoreClick={onLearnMoreClick}
        transactionRule={selectedTransaction?.content}
        transactionModifications={transactionModifications}
        open={showEditModal}
        onClose={setShowEditModal.setFalse}
        onSubmit={async (transactionRule, refresh) => {
          await editTransactionModification(
            {
              ...selectedTransaction,
              content: transactionRule,
            },
            refresh
          );
          setShowEditModal.setFalse();
        }}
      />
      <RuleDeletionAlert
        title="Delete data rule"
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete data rule"
        onConfirm={async () => {
          await deleteTransactionModification(selectedTransaction);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={
          <TransactionRulePreview
            transactionRule={selectedTransaction?.content}
          />
        }
      />
      <RuleUnlockAlert
        open={showUnlockAlert}
        onClose={setShowUnlockAlert.setFalse}
        onConfirm={async () => {
          await editTransactionModification({
            ...selectedTransaction,
            isLocked: false,
          });
          setShowUnlockAlert.setFalse();
        }}
      />
    </>
  );
};
